import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/cn';

/**
 * Outflanq-token Button. Variants follow shadcn/ui's pattern: primary
 * (accent fill), secondary (subtle bg), outline (border only), ghost
 * (text only), danger (destructive). Sizes match the input-row baseline
 * so buttons + inputs align horizontally without manual tweaking.
 */
const buttonVariants = cva(
  cn(
    'inline-flex items-center justify-center gap-sm whitespace-nowrap rounded-sm',
    'font-medium transition-colors duration-fast',
    'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2',
    'disabled:opacity-50 disabled:cursor-not-allowed',
  ),
  {
    variants: {
      variant: {
        primary: 'bg-accent text-on-accent hover:bg-accent-hover',
        secondary: 'bg-bg-tertiary text-text-primary border border-border hover:border-border-strong',
        outline: 'bg-transparent text-text-primary border border-border hover:border-border-strong hover:bg-bg-secondary',
        ghost: 'bg-transparent text-text-secondary hover:text-text-primary hover:bg-bg-secondary',
        success: 'bg-success text-cream hover:bg-success/90',
        danger: 'bg-transparent text-text-primary border border-border hover:border-danger hover:text-danger',
      },
      size: {
        sm: 'h-9 px-md text-caption',
        md: 'h-10 px-lg text-sm',
        lg: 'h-11 px-xl text-body',
        icon: 'h-9 w-9 p-0',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, type = 'button', ...props }, ref) => {
    return (
      <button
        ref={ref}
        type={type}
        className={cn(buttonVariants({ variant, size }), className)}
        {...props}
      />
    );
  },
);
Button.displayName = 'Button';

export { buttonVariants };
